'use strict';

import {
  addClassToElement,
  removeClassToElement
} from '@jspartials/class-list-utils';

// Elements
const html = document.querySelector('html');
const body = document.querySelector('body');

// Custom events
const customEvent = {
  listenProductTiles: 'listenProductTiles',
  reloadVtoButtons: 'reloadVtoButtons',
  listenAddonTiles: 'listenAddonTiles',
  loginAndSignupLoaded: 'loginAndSignupLoaded',
  qrGeneratorLoaded: 'qrGeneratorLoaded',
  initProductTilesAnimation: 'initProductTilesAnimation',
  adyen: {
    displayPaymentMethods: 'adyen::displayPaymentMethods',
    paymentMethodsUpdated: 'adyen::paymentMethodsUpdated'
  },
  klarna: {
    checkVisibilityOfPaymentMethod: 'checkVisibilityOfKlarnaPaymentMethod'
  },
  closeSearcherFromMenu: 'closeSearcherFromMenu',
  sidebar: {
    init: 'sidebar::init',
    close: 'sidebar::close',
    open: 'sidebar::open'
  },
  updatePickUpStoreSelectedForShipping: 'updatePickUpStoreSelectedForShipping',
  cart: {
    itemRemoved: 'cart::itemRemoved'
  },
  checkout: {
    selectShippingMethod: 'checkout::selectShippingMethod',
    selectStorePickupMethod: 'checkout::selectStorePickupMethod',
    afterRenderResumeOrderPage: 'checkout::afterRenderResumeOrderPage'
  },
  cookieBot: {
    onConsentReady: 'cookieBotOnConsentReady'
  }
};

// Media query strings
const mediaQuery = {
  tablet: '(min-width: 768px)'
};

const selectors = {
  fixedClass: 'tw-fixed',
  overflowHiddenClass: 'tw-overflow-hidden',
  fullWidthClass: 'tw-w-full'
};

var htmlElementScroll = 0;

/**
 * Get the current top location
 */
const currentYPosition = () => {
  // Firefox, Chrome, Opera, Safari
  if (self.pageYOffset) return self.pageYOffset;
  // Internet Explorer 6 - standards mode
  if (document.documentElement && document.documentElement.scrollTop)
    return document.documentElement.scrollTop;
  // Internet Explorer 6, 7 and 8
  if (document.body.scrollTop) return document.body.scrollTop;
  return 0;
};

/**
 * Get the position of element
 * @param {string} id of the element
 */
const elmYPosition = (id) => {
  const elm = document.querySelector(id);
  let y = elm.offsetTop;
  let node = elm;
  while (node.offsetParent && node.offsetParent !== document.body) {
    node = node.offsetParent;
    y += node.offsetTop;
  }
  return y;
};

/**
 * Get value from url parameter
 * @param {string} name of url parameter
 */
const getUrlParameter = (name) => {
  return (
    decodeURIComponent(
      (new RegExp(`[?|&]${name}=` + `([^&;]+?)(&|#|;|$)`).exec(
        location.search
      ) || [, ''])[1].replace(/\+/g, '%20')
    ) || null
  );
};

/**
 * Get value from session storage variable
 * @param {string} key of variable
 */
const getSessionStorage = (key = '') => {
  return sessionStorage.getItem(key);
};

/**
 * Set value for session storage variable
 * @param {string} key of variable
 * @param {string} value of variable
 */
const saveSessionStorage = (key = '', value = '') => {
  sessionStorage.setItem(key, value);
};

/**
 * Set HTML unscrollable
 */
const setHtmlUnscrollable = () => {
  htmlElementScroll = document.documentElement.scrollTop;
  addClassToElement(body, selectors.fixedClass);
  addClassToElement(body, selectors.overflowHiddenClass);
  addClassToElement(body, selectors.fullWidthClass);
  body.style.height = `calc(100% + ${htmlElementScroll}px)`;
  body.style.top = `-${htmlElementScroll}px`;
};

/**
 * Set HTML scrollable
 */
const setHtmlScrollable = () => {
  removeClassToElement(body, selectors.fixedClass);
  removeClassToElement(body, selectors.overflowHiddenClass);
  removeClassToElement(body, selectors.fullWidthClass);
  body.style.height = '';
  body.style.top = '';
  window.scrollTo({ top: htmlElementScroll, behavior: 'instant' });

  htmlElementScroll = 0;
};

const smoothScrollToAnchor = () => {
  const elementsWithHash = document.querySelectorAll('a[href*="#"]') || [];

  elementsWithHash.forEach((element) => {
    element.addEventListener('click', (event) => {
      event.preventDefault();
      const targetHref = element.getAttribute('href');
      let elementPosition = 0;
      if (targetHref != '#') {
        elementPosition = elmYPosition(targetHref);
      }
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    });
  });
};

const randomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min)) + min;
};

const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args);
    }, wait);
  };
};

const throttle = (func, wait) => {
  let waiting = false;
  return function () {
    if (waiting) {
      return;
    }

    waiting = true;
    setTimeout(() => {
      func.apply(this, arguments);
      waiting = false;
    }, wait);
  };
};

const getMediaQuery = (mediaQuery) => {
  return window.matchMedia(mediaQuery);
};

/**
 * Sleep for a certain amount of time
 * @param {number} ms - The amount of time to sleep in milliseconds
 * @returns {Promise} - The promise that resolves after the sleep
 */
const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Try to execute a function with retries and delay
 * @param {Function} func - The function to execute
 * @param {number} retries - The number of retries
 * @param {number} delay - The delay between retries in milliseconds
 * @param {Array} params - The parameters to pass to the function
 * @returns {Promise} - The promise that resolves after the function is executed
 */
const tryExecute = async (func, retries, delay, ...params) => {
  let attempts = 0;

  while (attempts < retries) {
    try {
      const response = await func(...params);

      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      const status = error.response?.status;
      const retryAfter = parseInt(
        error.response?.headers['retry-after'] || delay / 1000,
        10
      ); // in seconds

      if (status === 429) {
        // Too many requests
        await sleep(retryAfter * 1000);
      } else {
        if (attempts >= retries - 1) {
          throw error;
        }
      }
    }
    attempts++;
  }
};

const getPointingDevice = window.matchMedia(
  '(pointer:fine) and (hover: hover)'
);

const isPointingDevice = () => {
  return window.matchMedia('(pointer:fine) and (hover: hover)').matches;
};

/**
 * Add the element to the list of observed product tiles
 * @param {HTMLElement} element - The element to add into the list
 * @param {Array} list - The list to add the element
 * @returns {void}
 */
const addToList = (element, list) => {
  list.push(element);
};

export {
  html,
  body,
  getUrlParameter,
  getSessionStorage,
  saveSessionStorage,
  currentYPosition,
  elmYPosition,
  setHtmlUnscrollable,
  setHtmlScrollable,
  smoothScrollToAnchor,
  randomNumber,
  debounce,
  throttle,
  customEvent,
  mediaQuery,
  getMediaQuery,
  getPointingDevice,
  tryExecute,
  isPointingDevice,
  addToList
};
